import type { AbsichtElement, Answer, RunDraft, RunView } from '../shared/types.js';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message?: string
  ) {
    super(message || code);
  }
}

async function request<T>(method: 'GET' | 'POST', url: string, body?: unknown, keepalive = false): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: 'no-store',
    // A request that may outlive the page: the draft sent while the tab goes to the background.
    keepalive
  });
  const data = (await response.json().catch(() => ({}))) as { error?: string; message?: string } & T;
  if (!response.ok) throw new ApiError(data.error || 'generic', response.status, data.message);
  return data;
}

export const api = {
  start: (payload: { url?: string; consent?: boolean; fresh?: boolean; fixture?: string }) => request<{ id: string; cached: boolean; status: string }>('POST', '/api/run', payload),
  run: (id: string, token?: string | null) => request<RunView>('GET', `/api/run/${encodeURIComponent(id)}${token ? `?token=${encodeURIComponent(token)}` : ''}`),
  befund: (token: string) => request<RunView>('GET', `/api/befund/${encodeURIComponent(token)}`),
  draft: (id: string, draft: Partial<RunDraft>) => request<{ ok: boolean }>('POST', `/api/run/${encodeURIComponent(id)}/draft`, draft, true),
  proceed: (id: string, description: string | null) => request<RunView>('POST', `/api/run/${encodeURIComponent(id)}/proceed`, { description }),
  answers: (id: string, answers: Answer[], absicht: AbsichtElement[] | null) => request<RunView>('POST', `/api/run/${encodeURIComponent(id)}/answers`, { answers, absicht }),
  email: (id: string, email: string, marketing: boolean) => request<{ ok: boolean; delivery: string; link?: string }>('POST', `/api/run/${encodeURIComponent(id)}/email`, { email, marketing }),
  cta: (id: string) => request<{ ok: boolean }>('POST', `/api/run/${encodeURIComponent(id)}/cta`, {}),
  remove: (id: string) => request<{ ok: boolean }>('POST', `/api/run/${encodeURIComponent(id)}/delete`, {})
};

export function shotUrl(id: string, name: string | null | undefined): string | null {
  return name ? `/api/run/${encodeURIComponent(id)}/shot/${encodeURIComponent(name)}` : null;
}
